import { memo, useContext, useMemo, useState } from "react";
import {
  Box,
  createStyles,
  Link,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import { AccountCircleOutlined as AccountCircleOutlinedIcon } from "@material-ui/icons";
import { includes } from "lodash";
import { useQueryClient } from "react-query";

import {
  Countdown,
  CustomDialog,
  FavoriteIconButton,
  Image,
  Tag,
} from "components";
import { Color } from "components/Tag";
import { ASSET_DETAIL_URL } from "constants/index";
import { AssetDetail } from "containers";
import { breakpoints } from "containers/Responsive";
import { queryClient } from "containers/Root";
import { AppContext } from "contexts";
import { navigateTo } from "lib/helpers";
import * as routes from "routes";
import { AUCTION_USER_NOTIFICATION_TO_DISPLAY_NAME } from "services/auctions/constants";
import { OPEN_AUCTION_STATES } from "services/auctions/constants";
import { InventoryListing } from "services/inventory/types";
import { getUseListingsQueryKey } from "services/listings";
import {
  LISTING_DEAL_STATES,
  LISTING_KIND,
  LISTING_USER_NOTIFICATION_TO_DISPLAY_NAME,
} from "services/listings/constants";
import { useListingInfos } from "services/listings/hooks";
import { ListingState } from "services/listings/types";
import theme from "theme";

import { TTableColumn } from "../../types";

const useStyles = makeStyles(() =>
  createStyles({
    tableOrderRow: (props: { isColorBorder?: boolean }) => ({
      cursor: "pointer",
      height: "7.1875rem",
      ...(props.isColorBorder && {
        "& td:last-child": {
          borderRight: `1px solid ${theme.custom.palette.purple.main}`,
        },
        "& td:first-of-type": {
          borderLeft: `1px solid ${theme.custom.palette.purple.main}`,
        },
      }),
      "& .order-id": {
        color: theme.custom.palette.purple.main,
        "&:hover": {
          textDecoration: "underline",
        },
      },
    }),
    featuredLastRow: {
      border: `1px solid ${theme.custom.palette.purple.main} !important`,
      "& td": {
        padding: "0 !important",
        margin: "0 !important",
      },
    },
    expandedTableRow: (props: { isColorBorder?: boolean }) => ({
      ...(props.isColorBorder && {
        "& td:last-child": {
          borderRight: `1px solid ${theme.custom.palette.purple.main}`,
        },
        "& td:first-of-type": {
          borderLeft: `1px solid ${theme.custom.palette.purple.main}`,
        },
      }),
      "& td": {
        boxShadow: "inset 5px 5px 5px -5px rgba(0, 0, 0, 0.2)",
        backgroundColor: theme.custom.palette.softWhite.main,
        "& > div": {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          marginTop: theme.spacing(-1.25),
          "& > :not(:last-child)": {
            marginRight: theme.spacing(0.625),
          },
          "& > *": {
            marginTop: theme.spacing(1.25),
          },
        },
      },
    }),
    extraColumn: {
      width: "100%",
      "& span:first-of-type": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    expandedTableRowActions: {
      [`@media (min-width: ${breakpoints.desktop}px)`]: {
        marginLeft: "auto",
      },
    },
    leftTableCell: {
      cursor: "pointer",
      textAlign: "center",
      padding: "0 !important",
      borderRight: "1px solid #e1e1e3",
    },
    rightTableCell: {
      cursor: "pointer",
      textAlign: "right",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      "&&": {
        paddingLeft: theme.spacing(0.5),
      },
      "& img": {
        width: 12,
        [`@media (min-width: ${breakpoints.desktop}px)`]: {
          margin: "0 0 2px 8px",
        },
      },
    },
  }),
);

const LISTING_OR_AUCTION_USER_NOTIFICATION_TO_DISPLAY_NAME = {
  ...LISTING_USER_NOTIFICATION_TO_DISPLAY_NAME,
  ...AUCTION_USER_NOTIFICATION_TO_DISPLAY_NAME,
};
interface TagsProps {
  listing: InventoryListing;
  disableSystemTags: boolean;
  isColorBorder?: true;
}
const Tags = ({ listing, disableSystemTags, isColorBorder }: TagsProps) => {
  const classes = useStyles({ isColorBorder });
  const [isNewListing, isActiveOwner] = useListingInfos(
    listing.published_date,
    listing.owner_last_active_date,
  );

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const queryClient = useQueryClient();

  const systemTags = useMemo(() => {
    if (!disableSystemTags) {
      return (
        <>
          {listing.is_featured && (
            <Tag filled color="purple">
              Featured Listing
            </Tag>
          )}
          {isNewListing && includes(LISTING_KIND, listing.kind) && (
            <Tag filled color="green">
              New
            </Tag>
          )}
          {isActiveOwner && (
            <Tag filled color="green">
              Active Owner
            </Tag>
          )}
        </>
      );
    }
  }, [
    disableSystemTags,
    listing.is_featured,
    listing.kind,
    isActiveOwner,
    isNewListing,
  ]);

  let userNotificationColor: Color;
  if (
    [
      "awaiting_acceptance",
      "offer_accepted",
      "upcoming",
      "preview",
      "accepting_opening_bids",
      "live",
    ].includes(listing.user_notification)
  ) {
    userNotificationColor = "green";
  } else if (["pending"].includes(listing.user_notification)) {
    userNotificationColor = "yellow";
  } else if (
    ["fail_offer_rejected", "fail_offer_timeout", "outbid"].includes(
      listing.user_notification,
    )
  ) {
    userNotificationColor = "red";
  }

  return (
    <tr className={classes.expandedTableRow}>
      <td colSpan={100}>
        <div>
          {systemTags}
          {listing.is_nft && (
            <Tag filled color="purple">
              NFT
            </Tag>
          )}
          {listing.tags.length > 0 ? (
            listing.tags.map((tag: string) => <Tag key={tag}>{tag}</Tag>)
          ) : (
            <Tag
              style={isMobile ? { display: "none" } : { visibility: "hidden" }}
            >
              No tags.
            </Tag>
          )}
          <Box
            className={classes.expandedTableRowActions}
            style={isMobile ? { marginRight: "auto" } : { marginLeft: "auto" }}
          >
            {userNotificationColor && (
              <Tag color={userNotificationColor} data-testid="userNotification">
                {
                  LISTING_OR_AUCTION_USER_NOTIFICATION_TO_DISPLAY_NAME[
                    listing.user_notification
                  ]
                }
                {listing.user_notification === "live" && (
                  <>
                    {" "}
                    <Countdown
                      endTime={listing.close_date}
                      onEndTimeReached={() => {
                        queryClient.refetchQueries("inventory-listings");
                      }}
                    />
                  </>
                )}
              </Tag>
            )}
            {listing.user_notification === "set_price" && (
              <Link
                href={`${ASSET_DETAIL_URL}${listing.listing_id}/`}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
              >
                <Tag filled color="yellow">
                  Set List Price
                </Tag>
              </Link>
            )}
          </Box>
        </div>
      </td>
    </tr>
  );
};

interface TableRowProps {
  columns: TTableColumn<InventoryListing>[];
  listing: InventoryListing;
  prevUrl?: string;
  isColorBorder?: true;
}

const TableRow = memo(
  ({ columns, listing, prevUrl, isColorBorder }: TableRowProps) => {
    const classes = useStyles({ isColorBorder });
    const { user } = useContext(AppContext);
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const [isAssetDetailModalOpen, setIsAssetDetailModalOpen] = useState(false);

    const isListingOwner = user?.userId === listing.seller_id;
    const isListing = includes(LISTING_KIND, listing.kind);
    const isOpenListing =
      isListing && !includes(LISTING_DEAL_STATES, listing.state);
    const isOpenListingOrAuction =
      isOpenListing || includes(OPEN_AUCTION_STATES, listing.state);

    const [expandedRow, setExpandedRow] = useState(false);

    const handleSelectedAsset = (forceNavigate: boolean) => {
      if (forceNavigate) {
        listing.kind === "auction"
          ? navigateTo(listing.url, null, true)
          : navigateTo(`${ASSET_DETAIL_URL}${listing.listing_id}/`, null, true);
        return;
      }

      if (isMobile) {
        setExpandedRow(!expandedRow);
        return;
      }

      queryClient.refetchQueries(getUseListingsQueryKey(listing.listing_id));
      setIsAssetDetailModalOpen(true);
      window.history.replaceState(
        null,
        null,
        routes.orderBookAssetDetailPreview(listing.listing_id.toString()),
      );
    };

    const onModalClose = () => {
      queryClient.refetchQueries("inventory-listings");
      window.history.replaceState(null, null, prevUrl);
      setIsAssetDetailModalOpen(false);
    };

    return (
      <>
        <tr
          className={classes.tableOrderRow}
          style={expandedRow ? { backgroundColor: "#F5F5F5" } : undefined}
        >
          <td className={classes.leftTableCell} rowSpan={2}>
            {isListingOwner ? (
              <AccountCircleOutlinedIcon
                style={{ color: theme.custom.palette.green.main }}
              />
            ) : includes(LISTING_KIND, listing.kind) ? (
              <FavoriteIconButton
                listingId={listing.listing_id}
                listingState={listing.state as ListingState}
                removeBackground
              />
            ) : null}
          </td>

          {columns.map(column => (
            <td
              key={String(column.primaryKey)}
              style={{ textAlign: column.rightAlign ? "right" : "left" }}
              onClick={
                !column.preventSelect
                  ? () => handleSelectedAsset(listing.kind === "auction")
                  : undefined
              }
            >
              {column.format
                ? column.format(listing)
                : listing[column.primaryKey]}
            </td>
          ))}
          <td
            className={classes.rightTableCell}
            onClick={() => handleSelectedAsset(true)}
          >
            <Image name="orderbook-arrow-right.svg" />
          </td>
        </tr>
        <Tags
          listing={listing}
          disableSystemTags={!isOpenListingOrAuction}
          isColorBorder={isColorBorder}
        />
        {isAssetDetailModalOpen && (
          <CustomDialog
            onClose={onModalClose}
            title={
              listing.listing_id
                ? `Listing ID (${listing.listing_id})`
                : undefined
            }
            openInNew={() =>
              navigateTo(
                `${ASSET_DETAIL_URL}${listing.listing_id}/`,
                null,
                true,
              )
            }
          >
            {listing.listing_id && (
              <AssetDetail isModal={{ listingId: listing.listing_id }} />
            )}
          </CustomDialog>
        )}
      </>
    );
  },
);

export default TableRow;
